import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Collapse, DatePicker, Input, InputNumber, Popconfirm, Segmented, Select, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { addKnownCustomers, clearKnownCustomers, previewSlots } from "./calendarService";
import SlotPicker from "./SlotPicker";
import { TIME_OPTIONS, newServiceId } from "./calendarShared";
import spStyles from "../SonicProspect/style.module.scss";
import styles from "./style.module.scss";

const TIMEZONES = [
  "Europe/Istanbul",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Lisbon",
  "Europe/Belgrade",
  "Europe/Amsterdam",
  "Asia/Dubai",
  "Asia/Riyadh",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "UTC",
];
const LOCATION_TYPES = ["in_person", "phone", "video"];

/** Settings → More options: everything a small business rarely needs, one closed section each. */
const MoreOptions = ({ botId, cfg, update, meta, onMeta, connection, calendars, onPick, isCalendly, dirty, saving, onSave, onBack, t }) => {
  const avail = cfg.availability;
  const updateAvailability = (patch) => update({ availability: { ...avail, ...patch } });
  const tzOptions = (TIMEZONES.includes(avail.timezone) ? TIMEZONES : [avail.timezone, ...TIMEZONES]).map((z) => ({
    value: z,
    label: z.replace(/_/g, " "),
  }));
  const writable = calendars.filter((c) => c.writable);

  const items = [];
  if (!isCalendly) {
    items.push(
      {
        key: "timezone",
        label: t("sonic_calendar.timezone"),
        children: (
          <Select showSearch value={avail.timezone} options={tzOptions} onChange={(timezone) => updateAvailability({ timezone })} style={{ minWidth: 260 }} aria-label={t("sonic_calendar.timezone")} />
        ),
      },
      {
        key: "closed",
        label: t("sonic_calendar.more_closed"),
        children: <Overrides overrides={cfg.overrides || []} onChange={(overrides) => update({ overrides })} t={t} />,
      },
      {
        key: "services",
        label: t("sonic_calendar.more_services"),
        children: <Services services={cfg.services} onChange={(services) => update({ services })} t={t} />,
      },
      {
        key: "rules",
        label: t("sonic_calendar.rules_title"),
        children: (
          <div className={styles.grid3}>
            <NumberField label={t("sonic_calendar.min_notice")} value={avail.min_notice_min} max={20160} onChange={(v) => updateAvailability({ min_notice_min: v })} suffix="min" />
            <NumberField label={t("sonic_calendar.horizon")} value={avail.horizon_days} min={1} max={365} onChange={(v) => updateAvailability({ horizon_days: v })} suffix={t("sonic_calendar.days")} />
            <NumberField label={t("sonic_calendar.slot_step")} value={avail.slot_step_min} min={5} max={240} onChange={(v) => updateAvailability({ slot_step_min: v })} suffix="min" />
            <NumberField label={t("sonic_calendar.max_per_day")} value={avail.max_per_day} min={0} max={200} onChange={(v) => updateAvailability({ max_per_day: v || null })} placeholder={t("sonic_calendar.no_limit")} />
          </div>
        ),
      }
    );
  }
  if (connection && connection.provider !== "calendly" && calendars.length) {
    items.push({
      key: "calendars",
      label: t("sonic_calendar.more_calendars"),
      children: (
        <div className={styles.grid2}>
          <label>
            <span className={spStyles.fieldLabel}>{t("sonic_calendar.busy_calendars")}</span>
            <Select
              mode="multiple"
              value={connection.busy_calendar_ids}
              options={calendars.map((c) => ({ value: c.primary ? "primary" : c.id, label: c.name }))}
              onChange={(ids) => onPick({ busy: ids.length ? ids : ["primary"] })}
            />
          </label>
          <label>
            <span className={spStyles.fieldLabel}>{t("sonic_calendar.write_calendar")}</span>
            <Select value={connection.write_calendar_id} options={writable.map((c) => ({ value: c.primary ? "primary" : c.id, label: c.name }))} onChange={(write) => onPick({ write })} />
          </label>
        </div>
      ),
    });
  }
  items.push(
    {
      key: "details",
      label: t("sonic_calendar.more_details"),
      children: (
        <div className={styles.grid2}>
          <label>
            <span className={spStyles.fieldLabel}>{t("sonic_calendar.business_name")}</span>
            <Input value={cfg.business_name} onChange={(e) => update({ business_name: e.target.value })} />
          </label>
          <label>
            <span className={spStyles.fieldLabel}>{t("sonic_calendar.notify_email")}</span>
            <Input value={cfg.notify_email} placeholder="team@example.com" onChange={(e) => update({ notify_email: e.target.value })} />
          </label>
          <label>
            <span className={spStyles.fieldLabel}>{t("sonic_calendar.privacy_url")}</span>
            <Input value={cfg.privacy_url} placeholder="https://" onChange={(e) => update({ privacy_url: e.target.value })} />
          </label>
          <label className={styles.checkLabel}>
            <Checkbox checked={!!cfg.require_email} onChange={(e) => update({ require_email: e.target.checked })}>
              {t("sonic_calendar.require_email")}
            </Checkbox>
          </label>
        </div>
      ),
    },
    {
      key: "page",
      label: t("sonic_calendar.page_title"),
      children: (
        <>
          <p className={spStyles.handoffHint}>{t("sonic_calendar.page_desc")}</p>
          <Checkbox checked={!!cfg.booking_page} onChange={(e) => update({ booking_page: e.target.checked })}>
            {t("sonic_calendar.page_on")}
          </Checkbox>
        </>
      ),
    },
    {
      key: "customers",
      label: t("sonic_calendar.customers_title"),
      children: <KnownCustomers botId={botId} count={meta.knownCustomers} onCount={(n) => onMeta({ knownCustomers: n })} t={t} />,
    },
    {
      key: "preview",
      label: t("sonic_calendar.more_preview"),
      children: <PreviewSlots botId={botId} services={isCalendly ? meta.calendlyServices || [] : cfg.services} tz={avail.timezone} dirty={dirty} t={t} />,
    }
  );

  return (
    <div className={styles.simple}>
      <button type="button" className={styles.back} onClick={onBack}>
        ‹ {t("sonic_calendar.settings")}
      </button>
      <h1 className={styles.simpleTitle}>{t("sonic_calendar.more_options")}</h1>
      <Collapse items={items} className={styles.more} />
      <div className={styles.simpleFoot}>
        {meta.pricingUrl ? (
          <a href={meta.pricingUrl} target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
            {t("sonic_calendar.billing_link")}
          </a>
        ) : null}
        <Button type="primary" size="large" className={styles.cta} loading={saving} disabled={!dirty} onClick={onSave}>
          {t("sonic_calendar.save")}
        </Button>
      </div>
    </div>
  );
};

const NumberField = ({ label, value, onChange, min = 0, max, suffix, placeholder }) => (
  <label>
    <span className={spStyles.fieldLabel}>{label}</span>
    <InputNumber
      value={value}
      min={min}
      max={max}
      placeholder={placeholder}
      addonAfter={suffix}
      onChange={(v) => onChange(v === null ? null : Number(v))}
      style={{ width: "100%" }}
    />
  </label>
);

const Overrides = ({ overrides, onChange, t }) => {
  const add = (d) => {
    if (!d) return;
    const date = d.format("YYYY-MM-DD");
    if (overrides.some((o) => o.date === date)) return;
    onChange([...overrides, { date, closed: true, intervals: [], note: "" }].sort((a, b) => a.date.localeCompare(b.date)));
  };
  const set = (date, patch) => onChange(overrides.map((o) => (o.date === date ? { ...o, ...patch } : o)));
  return (
    <div className={styles.overrides}>
      <div className={styles.overridesHead}>
        <p className={spStyles.handoffHint}>{t("sonic_calendar.exceptions_desc")}</p>
        <DatePicker
          placeholder={t("sonic_calendar.add_exception")}
          disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
          onChange={add}
          value={null}
        />
      </div>
      {overrides.map((o) => (
        <div key={o.date} className={styles.dayRow}>
          <span className={styles.dayName}>{dayjs(o.date).format("ddd D MMM YYYY")}</span>
          <Segmented
            size="small"
            value={o.closed ? "closed" : "custom"}
            options={[
              { value: "closed", label: t("sonic_calendar.closed") },
              { value: "custom", label: t("sonic_calendar.custom_hours") },
            ]}
            onChange={(v) => set(o.date, v === "closed" ? { closed: true, intervals: [] } : { closed: false, intervals: o.intervals?.length ? o.intervals : [["10:00", "14:00"]] })}
          />
          {!o.closed
            ? (o.intervals || []).map((range, idx) => (
                <span key={idx} className={styles.range}>
                  <Select size="small" value={range[0]} options={TIME_OPTIONS} aria-label={t("sonic_calendar.from")} onChange={(v) => set(o.date, { intervals: o.intervals.map((r, i) => (i === idx ? [v, r[1]] : r)) })} />
                  <span>–</span>
                  <Select size="small" value={range[1]} options={TIME_OPTIONS} aria-label={t("sonic_calendar.to")} onChange={(v) => set(o.date, { intervals: o.intervals.map((r, i) => (i === idx ? [r[0], v] : r)) })} />
                </span>
              ))
            : null}
          <Input
            size="small"
            value={o.note}
            maxLength={120}
            placeholder={t("sonic_calendar.exception_note")}
            aria-label={t("sonic_calendar.exception_note")}
            onChange={(e) => set(o.date, { note: e.target.value })}
            style={{ width: 160 }}
          />
          <Button size="small" type="text" icon={<DeleteOutlined />} aria-label={t("sonic_calendar.remove")} onClick={() => onChange(overrides.filter((x) => x.date !== o.date))} />
        </div>
      ))}
    </div>
  );
};

const Services = ({ services, onChange, t }) => {
  const set = (id, patch) => onChange(services.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  return (
    <div className={styles.services}>
      {services.map((s) => (
        <div key={s.id} className={styles.service}>
          <div className={styles.grid2}>
            <label>
              <span className={spStyles.fieldLabel}>{t("sonic_calendar.service_name")}</span>
              <Input value={s.name} maxLength={80} onChange={(e) => set(s.id, { name: e.target.value })} />
            </label>
            <label>
              <span className={spStyles.fieldLabel}>{t("sonic_calendar.location_type")}</span>
              <Select
                value={s.location_type}
                options={LOCATION_TYPES.map((v) => ({ value: v, label: t(`sonic_calendar.location_${v}`) }))}
                onChange={(v) => set(s.id, { location_type: v })}
              />
            </label>
          </div>
          <div className={styles.grid3}>
            <label>
              <span className={spStyles.fieldLabel}>{t("sonic_calendar.duration")}</span>
              <InputNumber value={s.duration_min} min={5} max={480} step={15} addonAfter="min" onChange={(v) => set(s.id, { duration_min: Number(v) || 30 })} style={{ width: "100%" }} />
            </label>
            <label>
              <span className={spStyles.fieldLabel}>{t("sonic_calendar.buffer_before")}</span>
              <InputNumber value={s.buffer_before_min ?? 0} min={0} max={240} step={5} addonAfter="min" onChange={(v) => set(s.id, { buffer_before_min: Number(v) || 0 })} style={{ width: "100%" }} />
            </label>
            <label>
              <span className={spStyles.fieldLabel}>{t("sonic_calendar.buffer_after")}</span>
              <InputNumber value={s.buffer_after_min ?? 0} min={0} max={240} step={5} addonAfter="min" onChange={(v) => set(s.id, { buffer_after_min: Number(v) || 0 })} style={{ width: "100%" }} />
            </label>
          </div>
          <div className={styles.grid2}>
            <label>
              <span className={spStyles.fieldLabel}>{t(`sonic_calendar.location_value_${s.location_type || "in_person"}`)}</span>
              <Input value={s.location} maxLength={300} onChange={(e) => set(s.id, { location: e.target.value })} />
            </label>
            <label>
              <span className={spStyles.fieldLabel}>{t("sonic_calendar.service_description")}</span>
              <Input value={s.description} maxLength={500} onChange={(e) => set(s.id, { description: e.target.value })} />
            </label>
          </div>
          <div className={styles.serviceFoot}>
            <Checkbox checked={s.active !== false} onChange={(e) => set(s.id, { active: e.target.checked })}>
              {t("sonic_calendar.service_active")}
            </Checkbox>
            {services.length > 1 ? (
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onChange(services.filter((x) => x.id !== s.id))}>
                {t("sonic_calendar.remove")}
              </Button>
            ) : null}
          </div>
        </div>
      ))}
      {services.length < 20 ? (
        <Button
          icon={<PlusOutlined />}
          onClick={() =>
            onChange([
              ...services,
              { id: newServiceId(), name: "", duration_min: 30, location_type: "in_person", location: "", description: "", active: true, buffer_before_min: 0, buffer_after_min: 0 },
            ])
          }
        >
          {t("sonic_calendar.add_service")}
        </Button>
      ) : null}
    </div>
  );
};

const PreviewSlots = ({ botId, services, tz, dirty, t }) => {
  const active = useMemo(() => services.filter((s) => s.active !== false && s.name), [services]);
  const [serviceId, setServiceId] = useState(active[0]?.id);
  const [from, setFrom] = useState(dayjs());
  const [state, setState] = useState({ loading: false, slots: null, error: null });

  useEffect(() => {
    if (!serviceId && active[0]) setServiceId(active[0].id);
  }, [active, serviceId]);

  const run = async () => {
    setState({ loading: true, slots: null, error: null });
    try {
      const res = await previewSlots(botId, { serviceId, from: from.format("YYYY-MM-DD"), days: 7 });
      setState({ loading: false, slots: res.slots, error: null });
    } catch (err) {
      setState({ loading: false, slots: null, error: err.code });
    }
  };

  return (
    <div>
      <p className={spStyles.handoffHint}>{dirty ? t("sonic_calendar.preview_save_first") : t("sonic_calendar.preview_desc")}</p>
      <div className={styles.previewBar}>
        <Select value={serviceId} options={active.map((s) => ({ value: s.id, label: s.name }))} onChange={setServiceId} style={{ minWidth: 180 }} aria-label={t("sonic_calendar.service_name")} />
        <DatePicker value={from} onChange={(d) => d && setFrom(d)} allowClear={false} />
        <Button onClick={run} loading={state.loading} disabled={dirty || !serviceId}>
          {t("sonic_calendar.preview_run")}
        </Button>
      </div>
      {state.error ? <p className={styles.error}>{t(state.error === "calendar_unavailable" ? "sonic_calendar.calendar_unavailable" : "sonic_calendar.load_error")}</p> : null}
      {state.slots ? <SlotPicker slots={state.slots} tz={tz} emptyText={t("sonic_calendar.no_slots")} /> : null}
    </div>
  );
};

const KnownCustomers = ({ botId, count, onCount, t }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      const res = await addKnownCustomers(botId, text);
      onCount(res.count);
      setText("");
      message.success(t("sonic_calendar.customers_added", { count: res.added }));
    } catch (_) {
      message.error(t("sonic_calendar.save_error"));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    const res = await clearKnownCustomers(botId).catch(() => null);
    if (res) onCount(res.count);
  };

  return (
    <div>
      <p className={spStyles.handoffHint}>{t("sonic_calendar.customers_desc")}</p>
      <Input.TextArea
        rows={3}
        value={text}
        placeholder={t("sonic_calendar.customers_placeholder")}
        aria-label={t("sonic_calendar.customers_title")}
        onChange={(e) => setText(e.target.value)}
      />
      <div className={styles.actionsRow}>
        <Button onClick={add} loading={busy} disabled={!text.trim()}>
          {t("sonic_calendar.customers_add")}
        </Button>
        <span className={spStyles.handoffHint}>{t("sonic_calendar.customers_count", { count: count || 0 })}</span>
        {count ? (
          <Popconfirm title={t("sonic_calendar.customers_clear_confirm")} onConfirm={clear}>
            <Button type="text" danger>
              {t("sonic_calendar.customers_clear")}
            </Button>
          </Popconfirm>
        ) : null}
      </div>
    </div>
  );
};
export default MoreOptions;
